import { createFileRoute } from '@tanstack/react-router'
import * as React from 'react'
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryState,
} from 'nuqs'
import { PlusCircle } from 'lucide-react'
import { ClientsTable } from './-components/ClientsTable'
import { TeamManagementDialog } from './-components/TeamManagementDialog'
import { LinkNewClientDialog } from './-components/LinkNewClientDialog'
import { AddMissingClientWizard } from './-components/AddMissingClientWizard'
import { BulkNotifyActionBar } from './-components/BulkNotifyActionBar'
import { BulkNotifyDialog } from './-components/BulkNotifyDialog'
import { BulkApproveAnnualReviewsBar } from './-components/BulkApproveAnnualReviewsBar'
import { BulkApproveAnnualReviewsDialog } from './-components/BulkApproveAnnualReviewsDialog'
import {
  ACCOUNTS_COLUMN_ORDER,
  ACCOUNTS_COLUMN_VISIBILITY,
  ANNUAL_REVIEW_COLUMN_ORDER,
  ANNUAL_REVIEW_COLUMN_VISIBILITY,
  DEFAULT_COLUMN_ORDER,
  EXTRA_COLUMN_DEFAULT_VISIBILITY,
  ROTH_RADAR_COLUMN_ORDER,
  ROTH_RADAR_COLUMN_VISIBILITY,
  createAnnualReviewSelectColumn,
  createColumns,
  createRothRadarSelectColumn,
} from './-components/columns'
import type {
  Client,
  ClientArDueWithinFilter,
  ClientArStageFilter,
  ClientArStatusFilter,
  ClientExperienceType,
  ClientFilters,
  ClientPaidStatus,
  ClientStatus,
} from '@/types/client.types'
import type { RowSelectionState, SortingState } from '@tanstack/react-table'
import type { SystemTab } from '@/types/client-views.types'
import type {
  RothRadarAlertStatus,
  RothRadarConnectionHealthFilter,
  RothRadarFilterValue,
  RothRadarPlanFilter,
  RothRadarScopingFilter,
  RothRadarSetupState,
  RothRadarSignalState,
  RothRadarStrategy,
} from '@/types/rothRadar.types'
import { SaveViewDialog } from '@/components/shared/SaveViewDialog'
import { Button } from '@/components/ui/button'
import { PageTitle } from '@/components/PageTitle'
import { SaveStatusFab } from '@/components/clients/SaveStatusFab'
import { RothRadarPositionsSheet } from '@/components/shared/RothRadarPositionsSheet'
import { useAuth } from '@/contexts/AuthContext'
import {
  isAccountsMetricsRefreshing,
  useAllClients,
  useClientAccountsMetrics,
  useClientById,
  useToggleFavorite,
  useUpdateClient,
  useUpdateClientTeam,
} from '@/hooks/repositories/useClientsRepository'
import {
  useClientViews,
  useColumnOrder,
  useColumnVisibility,
} from '@/hooks/use-cases/useClientViews'
import { useDebouncedValue } from '@/hooks/use-cases/useDebouncedValue'
import { useUpdateClientStatus } from '@/hooks/use-cases/useUpdateClientStatus'
import { useRothRadarBook } from '@/hooks/use-cases/useRothRadarBook'
import {
  useExportRothRadarBook,
  useRothRadarMetrics,
  useRothRadarSelectableClients,
} from '@/hooks/repositories/useRothRadarMetrics'
import { useUpdateClientRothRadarStrategy } from '@/hooks/repositories/useClientRothRadarSettings'
import { useFeatureFlags } from '@/hooks/use-feature-flags'
import {
  consumeLinkNewClientRequest,
  useLinkNewClientRequested,
} from '@/store/linkNewClientRequestStore'

const ROTH_RADAR_TAB = 'roth-radar' as const
const ACCOUNTS_TAB = 'accounts' as const
const ANNUAL_REVIEW_TAB = 'annual-review' as const
// Hoisted to module scope so its default `[]` has a STABLE identity across
// renders. Built inline, nuqs returns a fresh `[]` every render when no
// advisor is selected, which made the "reset to page 1" effect (dep:
// advisorIds) fire on every render and snap pagination back to page 1 —
// breaking the Next button. Keep this at module scope.
const advisorsParser = parseAsArrayOf(parseAsString).withDefault([])
// Same pagination-safety rule as `advisorsParser` above — module scope keeps
// the default `[]` identity stable across renders.
const plannerIdsParser = parseAsArrayOf(parseAsString).withDefault([])
const specialistIdsParser = parseAsArrayOf(parseAsString).withDefault([])
const rcOwnerIdsParser = parseAsArrayOf(parseAsString).withDefault([])
// Same pagination-safety rule as `advisorsParser` above — module scope keeps
// the default `[]` identity stable across renders.
const schedulesParser = parseAsArrayOf(parseAsString).withDefault([])
const SYSTEM_TABS = [
  'all',
  'my',
  'team',
  'favorites',
  ROTH_RADAR_TAB,
  ACCOUNTS_TAB,
  ANNUAL_REVIEW_TAB,
] as const
// The last tab still paginating a book in the browser: the Annual Review
// rollup.
//
// Two tabs have left. The Accounts tab sources its cards from
// `GET /clients/metrics` (sc-1172), and the Roth Radar tab from
// `GET /roth-radar/metrics` plus a real server-side filter and sort on the
// clients query (sc-1329). Radar was the harder case and the one that broke:
// its tier and status weren't columns, so it fetched the book, POSTed every id
// to the summary endpoint, and filtered client-side — until the book outgrew
// that endpoint's 1000-id cap and the tab rendered empty in production. It now
// pages like every other tab.
// The Annual Review tab's `useAnnualReviewRollup` counts are computed over
// this page (sc-811 Part 2 followup bugfix): the assumption that
// "an advisor's book is at most a few hundred clients" stopped holding (the
// firm-wide book — the AR tab has no ownership scoping — is ~1,900+ and
// growing), so this bound was raised with real headroom. This remains a
// soft cap, not a true "fetch everything" guarantee — the durable fix is to
// have the Annual Review rollup consume a dedicated backend-aggregated
// endpoint (`GET /annual-reviews/metrics` already computes upcoming/perYear/
// perTier over the FULL AR-relevant population server-side, though it
// doesn't yet return the "Past"/overdue count the rollup's Past card needs)
// instead of paginating the whole book client-side. Revisit if the book
// count approaches this bound again.
const DASHBOARD_BOOK_PAGE_SIZE = 5000
// Maps TanStack Table column IDs to API sortBy values for server-side sort
const COLUMN_SORT_MAP: Partial<Record<string, ClientFilters['sortBy']>> = {
  createdAt: 'createdAt',
  firstName: 'firstName',
  lastName: 'lastName',
  escapePlanCreatedAt: 'escapePlanCreatedAt',
  escapePlanDeliveredAt: 'escapePlanDeliveredAt',
  nextMeetingDate: 'nextMeetingAt',
  rothRadarTargetProgress: 'rothRadarTargetProgress',
}

export const Route = createFileRoute('/_app/clients/')({
  component: RouteComponent,
})

function RouteComponent() {
  const { user } = useAuth()
  const { annualReviews } = useFeatureFlags()

  // --- Views ---
  const {
    views,
    defaultTab,
    saveView,
    updateView,
    deleteView,
    renameView,
    toggleDefault,
    getViewById,
  } = useClientViews()

  // --- URL state via nuqs ---
  // `tab` is kept on nuqs (rather than migrated to TanStack Router's
  // validateSearch) because this route manages ~15 other filter params via
  // nuqs's generic React adapter, which writes directly to
  // window.history and is NOT router-aware. A validateSearch-driven
  // `navigate({ search: (prev) => ... })` for `tab` would derive the new URL
  // from the router's own typed search state (which doesn't know about the
  // nuqs-owned params) and would wipe them from the URL on every tab change.
  // Instead we fix the actual deep-link bug — nuqs's default `clearOnDefault`
  // behavior strips `?tab=` from the URL whenever the value matches the
  // (user-specific, localStorage-derived) default tab, so a shared/bookmarked
  // `?tab=accounts` link silently loses its query param on first load once
  // that value happens to equal the viewer's default. `clearOnDefault: false`
  // keeps the param present at all times so every tab (system or custom
  // view) is reliably deep-linkable.
  const [activeTab, setActiveTab] = useQueryState(
    'tab',
    parseAsString
      .withDefault(defaultTab ?? 'all')
      .withOptions({ clearOnDefault: false }),
  )
  const [searchValue, setSearchValue] = useQueryState(
    'search',
    parseAsString.withDefault(''),
  )
  // Search box debounce (sc-1172) — the search input is bound to
  // `searchInput` (immediate, local) so typing stays responsive, while
  // `searchValue` (the nuqs-backed value that actually feeds `filters` /
  // `fullBookFilters` / `accountsMetricsFilters` below, and so the React
  // Query key) only catches up 300ms after the user stops typing. This keeps
  // a fresh paginated request + full-book request + accounts-metrics request
  // from firing on every keystroke.
  //
  // Programmatic search changes — applying a saved view or switching tabs —
  // call `setSearchValue` directly (unchanged below) and so land in
  // `searchValue` immediately, bypassing the debounce; the effect below then
  // re-syncs `searchInput` to match so the box doesn't show stale text.
  const [searchInput, setSearchInput] = React.useState(searchValue)
  const debouncedSearchInput = useDebouncedValue(searchInput, 300)
  React.useEffect(() => {
    if (debouncedSearchInput === searchValue) return
    void setSearchValue(debouncedSearchInput || null)
    // Deliberately keyed on `debouncedSearchInput` alone — this should only
    // fire once the debounced value settles, not on every `searchValue`
    // change (which would refire this on its own write above).
  }, [debouncedSearchInput])
  React.useEffect(() => {
    setSearchInput(searchValue)
  }, [searchValue])
  const [statusFilter, setStatusFilter] = useQueryState(
    'status',
    parseAsString.withDefault('all'),
  )
  const [advisorIds, setAdvisorIds] = useQueryState('advisors', advisorsParser)
  const [paidStatusFilter, setPaidStatusFilter] = useQueryState(
    'paidStatus',
    parseAsString,
  )
  const [experienceFilter, setExperienceFilter] = useQueryState(
    'experience',
    parseAsString,
  )
  const [meetingTypeFilter, setMeetingTypeFilter] = useQueryState(
    'meetingType',
    parseAsString,
  )
  const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useQueryState(
    'subscriptionStatus',
    parseAsString,
  )
  const [plannerIds, setPlannerIds] = useQueryState(
    'planners',
    plannerIdsParser,
  )
  const [specialistIds, setSpecialistIds] = useQueryState(
    'specialists',
    specialistIdsParser,
  )
  const [rcOwnerIds, setRcOwnerIds] = useQueryState(
    'rcOwners',
    rcOwnerIdsParser,
  )
  const [createdFromFilter, setCreatedFromFilter] = useQueryState(
    'createdFrom',
    parseAsString,
  )
  const [createdToFilter, setCreatedToFilter] = useQueryState(
    'createdTo',
    parseAsString,
  )
  const [schedulesFilter, setSchedulesFilter] = useQueryState(
    'schedules',
    schedulesParser,
  )
  const [epActivatedFromFilter, setEpActivatedFromFilter] = useQueryState(
    'epActivatedFrom',
    parseAsString,
  )
  const [epActivatedToFilter, setEpActivatedToFilter] = useQueryState(
    'epActivatedTo',
    parseAsString,
  )
  const [epDeliveredFromFilter, setEpDeliveredFromFilter] = useQueryState(
    'epDeliveredFrom',
    parseAsString,
  )
  const [epDeliveredToFilter, setEpDeliveredToFilter] = useQueryState(
    'epDeliveredTo',
    parseAsString,
  )
  // sc-811 Part 2 — Annual Review tab quick filters (not persisted in saved
  // views, same treatment as `needsAnnualReview`/`hasBilling` tab scoping).
  const [dueWithinFilter, setDueWithinFilter] = useQueryState(
    'dueWithin',
    parseAsStringLiteral(['30', '60', '90'] as const),
  )
  const [arYearFilter, setArYearFilter] = useQueryState(
    'arYear',
    parseAsInteger,
  )
  const [arStatusFilter, setArStatusFilter] = useQueryState(
    'arStatus',
    parseAsStringLiteral(['missing', 'completed'] as const),
  )
  // sc-1034 Phase 2 — Annual Review tab's "Stage" quick filter. Same
  // tab-scoped/not-persisted-in-saved-views treatment as `dueWithin`/
  // `arYear`/`arStatus` above, but independent of them: it narrows to the
  // client's CURRENT open AR cycle's real ledger stage (queued/in progress)
  // and ANDs with whichever due-window/year/overdue scope is active, rather
  // than replacing it.
  const [arStageFilter, setArStageFilter] = useQueryState(
    'arStage',
    parseAsStringLiteral(['queued', 'in_progress'] as const),
  )
  // sc-811 Part 2 (followup) — the AR tab's implicit "overdue" default was
  // previously silent: with no due-window/year quick filter selected, the
  // list applied `needsAnnualReview = true` (strictly overdue) with no visual
  // indication, which read as a contradiction against the rollup's whole-book
  // counts. This makes that default explicit + user-clearable.
  // sc-1034 (rollup reorder followup) — defaults to `false` now: the rollup's
  // "Past" card previously appeared pre-selected/highlighted on load with no
  // click, which read as a contradiction with the panel's other scopes (none
  // of which pre-select). Nothing is selected until the advisor clicks a
  // card; clicking "Past" still sets this `true` exactly as before (see
  // `handlePastCardClick` in `AnnualReviewRollup`), and the tab shows its
  // full set by default, with overdue rows highlighted instead
  // (`ClientsTable`/`columns.tsx`).
  const [overdueOnly, setOverdueOnly] = useQueryState(
    'overdueOnly',
    parseAsBoolean.withDefault(false),
  )
  // Determine if active tab is a system tab or a custom view
  const isSystemTab = (SYSTEM_TABS as ReadonlyArray<string>).includes(activeTab)
  const activeView = isSystemTab ? null : getViewById(activeTab)
  const clientType: SystemTab = isSystemTab
    ? (activeTab as SystemTab)
    : (activeView?.filters.clientType ?? 'all')

  const isRothRadarTab = activeTab === ROTH_RADAR_TAB
  const isAccountsTab = activeTab === ACCOUNTS_TAB
  // Annual Review tab is gated behind the annualReviews LD flag (sc-811 Part
  // 2 dark-launch). Every downstream fetch/pagination/column branch keys off
  // this, not the raw tab value, so toggling the flag off hides the tab's
  // data even for the one render before the redirect effect below fires.
  const isAnnualReviewTab =
    activeTab === ANNUAL_REVIEW_TAB && Boolean(annualReviews)
  // The ownership scope actually sent to the API — the system tabs backed by
  // client-side/mock aggregation (Roth Radar, Accounts, Annual Review) have no
  // ownership scoping of their own, so they resolve to the firm-wide 'all'.
  // Hoisted out of the `filters` memo below (unchanged logic — checks
  // `clientType` directly, not `isRothRadarTab` etc.).
  const apiClientType =
    clientType === ROTH_RADAR_TAB ||
    clientType === ACCOUNTS_TAB ||
    clientType === ANNUAL_REVIEW_TAB
      ? 'all'
      : clientType
  // Roth Radar and Annual Review need the advisor's full (quick-filtered)
  // book rather than just the current server page — Roth Radar to
  // filter/paginate its tier/status client-side, and Annual Review to
  // compute its 30/60/90-day + per-year + per-tier KPI cards client-side
  // (mirrors `AnnualReviewMetricsService` on the backend). The Accounts tab
  // no longer needs it — its rollup is server-aggregated (sc-1172, see
  // `useClientAccountsMetrics` below).
  // sc-1329 — the Roth Radar tab left this club when it moved server-side; the
  // Annual Review rollup is the last thing still paginating a book in the
  // browser.
  const isFullBookTab = isAnnualReviewTab

  // If the Annual Review tab is gated off by its annualReviews LD flag
  // (sc-811 Part 2 dark-launch) but the URL still points at it — a stale link
  // or a saved default — fall back to the default tab instead of rendering
  // AR-only content behind a hidden tab button.
  React.useEffect(() => {
    if (!annualReviews && activeTab === ANNUAL_REVIEW_TAB) {
      const fallbackTab =
        defaultTab === ANNUAL_REVIEW_TAB ? 'all' : (defaultTab ?? 'all')
      void setActiveTab(fallbackTab)
    }
  }, [annualReviews, activeTab, defaultTab, setActiveTab])

  // Extra columns (experience, paid, etc.) are hidden by default for all
  // users — the Accounts, Roth Radar, and Annual Review tabs instead default
  // to their own curated column sets.
  const columnDefaults = isRothRadarTab
    ? ROTH_RADAR_COLUMN_VISIBILITY
    : isAccountsTab
      ? ACCOUNTS_COLUMN_VISIBILITY
      : isAnnualReviewTab
        ? ANNUAL_REVIEW_COLUMN_VISIBILITY
        : EXTRA_COLUMN_DEFAULT_VISIBILITY

  // --- Column visibility (persisted per tab/view) ---
  const { columnVisibility, setColumnVisibility } = useColumnVisibility(
    activeTab,
    activeView?.columnVisibility,
    columnDefaults,
  )

  // --- Column order (persisted per tab) ---
  // Accounts, Roth Radar, and Annual Review default to their own curated
  // orders; other tabs use the natural column-def order so their layout is
  // unchanged. No reorder UI yet — this only seeds the default render order
  // (drag-to-reorder is a follow-up).
  const columnOrderDefaults = isRothRadarTab
    ? ROTH_RADAR_COLUMN_ORDER
    : isAccountsTab
      ? ACCOUNTS_COLUMN_ORDER
      : isAnnualReviewTab
        ? ANNUAL_REVIEW_COLUMN_ORDER
        : DEFAULT_COLUMN_ORDER
  const { columnOrder, setColumnOrder } = useColumnOrder(
    activeTab,
    columnOrderDefaults,
  )

  // Apply saved view filters on initial load (e.g. navigating to /clients with a default view)
  const initializedRef = React.useRef(false)
  React.useEffect(() => {
    if (initializedRef.current) return
    initializedRef.current = true

    if (activeView) {
      void setSearchValue(activeView.filters.searchValue || null)
      void setStatusFilter(activeView.filters.statusFilter || 'all')
      void setAdvisorIds(activeView.filters.advisorIds)
      void setPaidStatusFilter(activeView.filters.paidStatus ?? null)
      void setExperienceFilter(activeView.filters.experience ?? null)
      void setMeetingTypeFilter(activeView.filters.meetingType ?? null)
      void setSubscriptionStatusFilter(
        activeView.filters.subscriptionStatus ?? null,
      )
      void setPlannerIds(activeView.filters.plannerIds ?? [])
      void setSpecialistIds(activeView.filters.specialistIds ?? [])
      void setRcOwnerIds(activeView.filters.rcOwnerIds ?? [])
      void setCreatedFromFilter(activeView.filters.createdFrom ?? null)
      void setCreatedToFilter(activeView.filters.createdTo ?? null)
      void setSchedulesFilter(activeView.filters.schedules ?? [])
      void setEpActivatedFromFilter(activeView.filters.epActivatedFrom ?? null)
      void setEpActivatedToFilter(activeView.filters.epActivatedTo ?? null)
      void setEpDeliveredFromFilter(activeView.filters.epDeliveredFrom ?? null)
      void setEpDeliveredToFilter(activeView.filters.epDeliveredTo ?? null)
    }
  }, [
    activeView,
    setSearchValue,
    setStatusFilter,
    setAdvisorIds,
    setPaidStatusFilter,
    setExperienceFilter,
    setMeetingTypeFilter,
    setSubscriptionStatusFilter,
    setPlannerIds,
    setSpecialistIds,
    setRcOwnerIds,
    setCreatedFromFilter,
    setCreatedToFilter,
    setSchedulesFilter,
    setEpActivatedFromFilter,
    setEpActivatedToFilter,
    setEpDeliveredFromFilter,
    setEpDeliveredToFilter,
  ])

  // --- Local state ---
  const [currentPage, setCurrentPage] = React.useState(1)
  const [pageSize, setPageSize] = React.useState(10)
  const [sortBy, setSortBy] = React.useState<ClientFilters['sortBy']>(undefined)
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc' | undefined>(
    undefined,
  )
  const [managingTeamClient, setManagingTeamClient] =
    React.useState<Client | null>(null)
  const [teamDialogOpen, setTeamDialogOpen] = React.useState(false)
  // Client whose holdings the Roth Radar positions drawer shows. Kept set
  // after close so the sheet's exit animation still has a name to render.
  const [positionsClient, setPositionsClient] = React.useState<Client | null>(
    null,
  )
  const [positionsSheetOpen, setPositionsSheetOpen] = React.useState(false)
  const [animatingClientId, setAnimatingClientId] = React.useState<
    string | null
  >(null)
  const [animatingWasFavorited, setAnimatingWasFavorited] = React.useState<
    boolean | undefined
  >(undefined)
  const [linkDialogOpen, setLinkDialogOpen] = React.useState(false)
  const [createdClientId, setCreatedClientId] = React.useState<string | null>(
    null,
  )
  // Email prefill for whichever "Link New Client" dialog opens next — set
  // only when the Cmd+K search palette's "Link a new client for '{query}'"
  // action handed off a query that looks like an email (see the
  // `linkNewClientRequested` effect below). `undefined` for the normal
  // "Link New Client" button click, so that path still starts blank.
  const [linkPrefillEmail, setLinkPrefillEmail] = React.useState<
    string | undefined
  >(undefined)
  // Annual Review tab hero actions (sc-811 Part 2) — lifted up from
  // `AnnualReviewRollup` so they can live in the page hero next to (in place
  // of) "Link New Client" instead of inside the rollup panel itself.
  const [wizardOpen, setWizardOpen] = React.useState(false)
  // Cmd+K client search palette's "Link a new client" quick action (its
  // no-results state, see `ClientSearchPalette`) can't open this page's
  // wizard directly — the palette is mounted app-wide in `AppHeader`, while
  // the wizard lives here. It flags the request on `linkNewClientRequestStore`
  // (a plain external store, not a URL param — an earlier `?linkClient=true`
  // nuqs version of this only opened the dialog after some OTHER nuqs setter
  // on this page ran, e.g. clicking a tab, because nuqs didn't re-read the
  // URL on landing). This effect fires as soon as the flag is set, opens
  // whichever dialog the "Link New Client" button itself would (same
  // `annualReviews`-gated branch below), and consumes the request so it
  // doesn't refire.
  //
  // `annualReviews` resolves asynchronously (LaunchDarkly) — while it's
  // still `undefined`, the request is deliberately left pending rather than
  // falling through to the `LinkNewClientDialog` branch, so a request that
  // arrives before the flag has settled still opens the correct dialog once
  // it does.
  const linkNewClientRequested = useLinkNewClientRequested()
  React.useEffect(() => {
    if (!linkNewClientRequested) return
    if (annualReviews === undefined) return
    // Consuming returns the palette's search string at request time (if
    // any). Only treat it as an email prefill when it actually looks like
    // one (contains "@") — the advisor may have searched by name instead,
    // and a bare name isn't a useful email prefill.
    const query = consumeLinkNewClientRequest()
    const emailPrefill = query?.includes('@') ? query : undefined
    setLinkPrefillEmail(emailPrefill)
    if (annualReviews) {
      setWizardOpen(true)
    } else {
      setLinkDialogOpen(true)
    }
  }, [linkNewClientRequested, annualReviews])
  const [saveDialogOpen, setSaveDialogOpen] = React.useState(false)
  const [isEditMode, setIsEditMode] = React.useState(false)
  const [rothRadarTierFilter, setRothRadarTierFilter] =
    React.useState<RothRadarFilterValue<RothRadarSignalState>>('ALL')
  const [rothRadarStatusFilter, setRothRadarStatusFilter] =
    React.useState<RothRadarFilterValue<RothRadarAlertStatus>>('ALL')
  const [rothRadarSetupFilter, setRothRadarSetupFilter] =
    React.useState<RothRadarFilterValue<RothRadarSetupState>>('ALL')
  const [rothRadarConnectionFilter, setRothRadarConnectionFilter] =
    React.useState<RothRadarFilterValue<RothRadarConnectionHealthFilter>>('ALL')
  const [rothRadarPlanFilter, setRothRadarPlanFilter] =
    React.useState<RothRadarFilterValue<RothRadarPlanFilter>>('ALL')
  const [rothRadarScopingFilter, setRothRadarScopingFilter] =
    React.useState<RothRadarFilterValue<RothRadarScopingFilter>>('ALL')
  // Roth Radar bulk-notify selection. A checked client the advisor can no
  // longer see is a client they didn't knowingly agree to email — the two
  // effects below clear this on every filter/tab change AND every page
  // change (Next/Previous doesn't go through the filter-reset effect since
  // it doesn't touch any of that effect's dependencies).
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({})
  const [bulkNotifyOpen, setBulkNotifyOpen] = React.useState(false)
  // sc-1514 — Annual Review tab's bulk-approve confirm dialog. It reuses
  // `rowSelection` above: only one tab's `select` column is ever mounted and
  // the selection clears on tab change, so a Roth Radar selection can't leak
  // in as an AR approval or vice versa.
  const [bulkApproveOpen, setBulkApproveOpen] = React.useState(false)

  // Track save status for the FAB
  const { status: saveStatus } = useUpdateClientStatus()

  // Cmd+S / Ctrl+S — blur active element to commit any focused editable cell
  React.useEffect(() => {
    if (!isEditMode) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isEditMode])

  const handleFabSave = React.useCallback(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
  }, [])

  // Fetch the newly created client to pass to TeamManagementDialog
  const { data: createdClient } = useClientById(createdClientId ?? '')

  // When createdClient data arrives, open the team dialog
  React.useEffect(() => {
    if (createdClient && createdClientId) {
      setManagingTeamClient(createdClient)
      setTeamDialogOpen(true)
    }
  }, [createdClient, createdClientId])

  // When switching to a custom view, load its saved filters into URL state
  const handleTabChange = React.useCallback(
    (tab: string) => {
      const view = (SYSTEM_TABS as ReadonlyArray<string>).includes(tab)
        ? null
        : getViewById(tab)

      if (view) {
        // Load saved view filters — restore ALL persisted filters (including
        // paid/experience/meeting type/subscription status) so a saved view
        // is a full snapshot.
        void setActiveTab(tab)
        void setSearchValue(view.filters.searchValue || null)
        void setStatusFilter(view.filters.statusFilter || 'all')
        void setAdvisorIds(view.filters.advisorIds)
        void setPaidStatusFilter(view.filters.paidStatus ?? null)
        void setExperienceFilter(view.filters.experience ?? null)
        void setMeetingTypeFilter(view.filters.meetingType ?? null)
        void setSubscriptionStatusFilter(
          view.filters.subscriptionStatus ?? null,
        )
        void setPlannerIds(view.filters.plannerIds ?? [])
        void setSpecialistIds(view.filters.specialistIds ?? [])
        void setRcOwnerIds(view.filters.rcOwnerIds ?? [])
        void setCreatedFromFilter(view.filters.createdFrom ?? null)
        void setCreatedToFilter(view.filters.createdTo ?? null)
        void setSchedulesFilter(view.filters.schedules ?? [])
        void setEpActivatedFromFilter(view.filters.epActivatedFrom ?? null)
        void setEpActivatedToFilter(view.filters.epActivatedTo ?? null)
        void setEpDeliveredFromFilter(view.filters.epDeliveredFrom ?? null)
        void setEpDeliveredToFilter(view.filters.epDeliveredTo ?? null)
        // Annual Review quick filters are tab-scoped, not part of a saved view.
        void setDueWithinFilter(null)
        void setArYearFilter(null)
        void setArStatusFilter(null)
        void setArStageFilter(null)
      } else {
        // System tab — clear filters
        void setActiveTab(tab)
        void setSearchValue(null)
        void setStatusFilter('all')
        void setAdvisorIds([])
        void setPaidStatusFilter(null)
        void setExperienceFilter(null)
        void setMeetingTypeFilter(null)
        void setSubscriptionStatusFilter(null)
        void setPlannerIds([])
        void setSpecialistIds([])
        void setRcOwnerIds([])
        void setCreatedFromFilter(null)
        void setCreatedToFilter(null)
        void setSchedulesFilter([])
        void setEpActivatedFromFilter(null)
        void setEpActivatedToFilter(null)
        void setEpDeliveredFromFilter(null)
        void setEpDeliveredToFilter(null)
        void setDueWithinFilter(null)
        void setArYearFilter(null)
        void setArStatusFilter(null)
        void setArStageFilter(null)
      }
      setCurrentPage(1)
    },
    [
      getViewById,
      setActiveTab,
      setSearchValue,
      setStatusFilter,
      setAdvisorIds,
      setPaidStatusFilter,
      setExperienceFilter,
      setMeetingTypeFilter,
      setSubscriptionStatusFilter,
      setPlannerIds,
      setSpecialistIds,
      setRcOwnerIds,
      setCreatedFromFilter,
      setCreatedToFilter,
      setSchedulesFilter,
      setEpActivatedFromFilter,
      setEpActivatedToFilter,
      setEpDeliveredFromFilter,
      setEpDeliveredToFilter,
      setDueWithinFilter,
      setArYearFilter,
      setArStatusFilter,
      setArStageFilter,
    ],
  )

  // Build filters object
  const filters: ClientFilters = React.useMemo(() => {
    const f: ClientFilters = {}

    if (searchValue) f.search = searchValue
    if (statusFilter !== 'all') f.status = statusFilter as ClientStatus
    if (apiClientType !== 'all') f.clientType = apiClientType
    if (advisorIds.length > 0) f.advisorIds = advisorIds
    if (plannerIds.length > 0) f.plannerIds = plannerIds
    if (specialistIds.length > 0) f.specialistIds = specialistIds
    if (rcOwnerIds.length > 0) f.rcOwnerIds = rcOwnerIds
    if (paidStatusFilter) f.paidStatus = paidStatusFilter as ClientPaidStatus
    if (experienceFilter)
      f.experience = experienceFilter as ClientExperienceType
    if (meetingTypeFilter) f.meetingType = meetingTypeFilter
    if (subscriptionStatusFilter) {
      f.subscriptionStatus =
        subscriptionStatusFilter as ClientFilters['subscriptionStatus']
    }
    if (createdFromFilter) f.createdFrom = createdFromFilter
    if (createdToFilter) f.createdTo = createdToFilter
    if (schedulesFilter.length > 0) f.schedules = schedulesFilter
    if (epActivatedFromFilter) f.epActivatedFrom = epActivatedFromFilter
    if (epActivatedToFilter) f.epActivatedTo = epActivatedToFilter
    if (epDeliveredFromFilter) f.epDeliveredFrom = epDeliveredFromFilter
    if (epDeliveredToFilter) f.epDeliveredTo = epDeliveredToFilter
    if (sortBy) {
      f.sortBy = sortBy
      f.sortDir = sortDir
    }
    // Accounts tab is scoped to clients with a billing artifact — implicit
    // tab scope, not a user-facing filter, so it's not persisted in saved views.
    if (isAccountsTab) f.hasBilling = true
    if (isAnnualReviewTab) {
      // Annual Review tab: the rollup's "Due 30/60/90" cards and Year +
      // Missing/Completed toggle are precise quick filters that REPLACE the
      // tab's default "needs review" scope when selected (they're mutually
      // exclusive with it — `dueWithin` is strictly forward-looking, while
      // `needsAnnualReview` is strictly overdue). With neither selected, the
      // tab falls back to `overdueOnly` (default `false` since sc-1034's
      // rollup reorder — user-settable via the "Past" card) — when that's
      // off, no AR scope is applied at all and the tab shows its full set.
      if (dueWithinFilter) {
        f.dueWithin = Number(dueWithinFilter) as ClientArDueWithinFilter
      } else if (arYearFilter != null && arStatusFilter) {
        f.arYear = arYearFilter
        f.arStatus = arStatusFilter
      } else if (overdueOnly) {
        f.needsAnnualReview = true
      }
      // "Stage" quick filter (sc-1034 Phase 2) — independent of the
      // due-window/year/overdue scope above; ANDs with whichever of those is
      // active rather than replacing it (e.g. "queued, due in 30 days").
      if (arStageFilter) {
        f.arStage = arStageFilter as ClientArStageFilter
      }
    }
    // sc-1329 — the Roth Radar tab's own narrowing, now server-side. Its two
    // card filters go on the wire; `rothRadarEntitledOnly` goes on every radar
    // request, because a client who can't be notified carries no signal and the
    // tab has never listed one.
    if (isRothRadarTab) {
      f.rothRadarEntitledOnly = true
      if (rothRadarTierFilter !== 'ALL') {
        f.rothRadarSignalState = rothRadarTierFilter
      }
      if (rothRadarStatusFilter !== 'ALL') {
        f.rothRadarAlertStatus = rothRadarStatusFilter
      }
      if (rothRadarSetupFilter !== 'ALL') {
        f.rothRadarSetupState = rothRadarSetupFilter
      }
      if (rothRadarConnectionFilter !== 'ALL') {
        f.rothRadarConnectionHealth = rothRadarConnectionFilter
      }
      if (rothRadarPlanFilter !== 'ALL') {
        f.rothRadarPlan = rothRadarPlanFilter
      }
      if (rothRadarScopingFilter !== 'ALL') {
        f.rothRadarScoping = rothRadarScopingFilter
      }
      // Signal first, then dollars, then newest — the comparator the browser
      // used to run over the downloaded book, now an ORDER BY. Only applied
      // when the advisor hasn't picked a column sort of their own.
      if (!sortBy) f.sortBy = 'rothRadarSignal'
    }

    f.page = currentPage
    f.perPage = pageSize

    return f
  }, [
    isRothRadarTab,
    rothRadarTierFilter,
    rothRadarStatusFilter,
    rothRadarSetupFilter,
    rothRadarConnectionFilter,
    rothRadarPlanFilter,
    rothRadarScopingFilter,
    searchValue,
    statusFilter,
    apiClientType,
    advisorIds,
    plannerIds,
    specialistIds,
    rcOwnerIds,
    paidStatusFilter,
    experienceFilter,
    meetingTypeFilter,
    subscriptionStatusFilter,
    createdFromFilter,
    createdToFilter,
    schedulesFilter,
    epActivatedFromFilter,
    epActivatedToFilter,
    epDeliveredFromFilter,
    epDeliveredToFilter,
    isAccountsTab,
    isAnnualReviewTab,
    dueWithinFilter,
    arYearFilter,
    arStatusFilter,
    arStageFilter,
    overdueOnly,
    annualReviews,
    sortBy,
    sortDir,
    currentPage,
    pageSize,
  ])

  // Reset to page 1 when filters or sort changes
  React.useEffect(() => {
    setCurrentPage(1)
    // Same trigger set as a genuine "what the advisor is looking at changed"
    // — the bulk-notify selection must not survive it. See the page-change
    // effect below for the other half (Next/Previous, which doesn't touch
    // any of these deps).
    setRowSelection({})
  }, [
    searchValue,
    statusFilter,
    activeTab,
    advisorIds,
    plannerIds,
    specialistIds,
    rcOwnerIds,
    rothRadarStatusFilter,
    rothRadarTierFilter,
    rothRadarSetupFilter,
    rothRadarConnectionFilter,
    rothRadarPlanFilter,
    rothRadarScopingFilter,
    paidStatusFilter,
    experienceFilter,
    meetingTypeFilter,
    subscriptionStatusFilter,
    createdFromFilter,
    createdToFilter,
    schedulesFilter,
    epActivatedFromFilter,
    epActivatedToFilter,
    epDeliveredFromFilter,
    epDeliveredToFilter,
    dueWithinFilter,
    arYearFilter,
    arStatusFilter,
    arStageFilter,
    overdueOnly,
    sortBy,
    sortDir,
  ])

  // Clears the bulk-notify selection on manual pagination (Next/Previous),
  // which sets `currentPage` directly and doesn't run the filter-reset
  // effect above.
  React.useEffect(() => {
    setRowSelection({})
  }, [currentPage])

  // Rows-per-page change: reset to page 1 and clear the selection (same
  // "can no longer see it" rationale as page change above).
  const handlePageSizeChange = React.useCallback((size: number) => {
    setPageSize(size)
    setCurrentPage(1)
    setRowSelection({})
  }, [])

  // Fetch clients — standard server-side pagination, used for EVERY tab
  // including Roth Radar since sc-1329. That tab used to be the exception: its
  // tier and status weren't columns, so it fetched the whole book and filtered,
  // sorted and paged it in the browser. They are now a derived filter and sort
  // on this query, so it pages like everything else.
  const {
    data: paginatedClients = {
      items: [],
      total: 0,
      page: 1,
      perPage: 10,
      totalPages: 1,
    },
    isLoading,
    // sc-811 Part 2 followup bugfix — a failed fetch (e.g. a stale cached
    // error from an earlier transient 500, or a genuine validation error on
    // an AR quick-filter param) previously fell through `paginatedClients`'s
    // default fallback value above and rendered as an indistinguishable
    // "No clients found" empty state. Surfaced via `ClientsTable`'s
    // `isError`/`onRetry` instead so a failed request is visibly a failure.
    isError: isClientsError,
    refetch: refetchClients,
  } = useAllClients(user?.databaseId, filters)

  // Annual Review tab: fetch the full (unpaginated) book so the rollup counts
  // reflect the whole book rather than the current server page. Disabled
  // outside that tab. The Accounts tab left this in sc-1172
  // (`useClientAccountsMetrics` below) and the Roth Radar tab in sc-1329
  // (`useRothRadarMetrics`); Annual Review is the last one here.
  //
  // The Annual Review tab's own AR-scope keys (`needsAnnualReview` /
  // `dueWithin` / `arYear` + `arStatus`) are deliberately DROPPED here rather
  // than spread through like every other quick filter: the rollup's KPI
  // cards (Due 30/60/90, per-year Missing/Completed, tier counts) are what
  // LETS you choose one of those scopes, so they must be computed over the
  // full AR-relevant population first — narrowed only by the general
  // ownership/search filters, mirroring `AnnualReviewMetricsService`'s own
  // "escapePlanDate not null, further narrowed by owner/rcOwner only"
  // population rule. Keeping the tab's own scope in this fetch would make
  // e.g. the "Due 30/60/90" cards read 0 while the default overdue-only
  // scope is active (an already-overdue client can never be "due within N
  // days" going forward).
  const fullBookFilters: ClientFilters = React.useMemo(() => {
    // `ClientsService.findAll` defaults to `ORDER BY firstName ASC` when
    // NEITHER `sort` nor `sortBy` is supplied — an arbitrary order with no
    // relationship to recency or AR relevance. This full-book fetch never
    // sent either (nothing in `filters` above sets them unless the user
    // manually sorts a column), so it silently inherited that default (sc-811
    // Part 2 followup bugfix): two known AR-overdue clients ranked #1/#3 by
    // `createdAt desc` sat at rank #1189/#1759 by `firstName asc` in the
    // ~1,900-client book, landing outside this (bounded, see
    // `DASHBOARD_BOOK_PAGE_SIZE` above) page entirely and vanishing from the
    // rollup's "Past" count. `sort: 'createdAtDesc'` restores a deterministic
    // newest-first default — harmless when the user HAS picked an explicit
    // column sort, since `sortBy` always takes precedence over `sort`
    // server-side.
    const sortFallback: Pick<ClientFilters, 'sort'> = { sort: 'createdAtDesc' }
    const {
      needsAnnualReview: _needsAnnualReview,
      dueWithin: _dueWithin,
      arYear: _arYear,
      arStatus: _arStatus,
      arStage: _arStage,
      ...rest
    } = filters
    return {
      ...sortFallback,
      ...rest,
      page: 1,
      perPage: DASHBOARD_BOOK_PAGE_SIZE,
    }
  }, [filters, isAnnualReviewTab])

  const {
    data: fullBook = {
      items: [],
      total: 0,
      page: 1,
      perPage: DASHBOARD_BOOK_PAGE_SIZE,
      totalPages: 1,
    },
    isLoading: isLoadingFullBook,
  } = useAllClients(
    isFullBookTab ? user?.databaseId : undefined,
    fullBookFilters,
  )

  // Accounts tab rollup filters (sc-1172) — the SAME population the table
  // scopes to (advisor's quick-filtered book, narrowed by `hasBilling` via
  // `filters` above), minus:
  //  - the four card-selectable keys (`experience`/`paidStatus`/
  //    `subscriptionStatus`/`meetingType`) — a card narrows the TABLE, not
  //    itself, so the cards must stay computed over the population they were
  //    clicked from, not the population their own click just produced.
  //  - `page`/`perPage`/`sort`/`sortBy`/`sortDir` — the query key is the
  //    whole filters object, so leaving these in would refetch the aggregate
  //    on every page click and every column sort even though the underlying
  //    population hasn't changed.
  const accountsMetricsFilters: ClientFilters = React.useMemo(() => {
    const {
      experience: _experience,
      paidStatus: _paidStatus,
      subscriptionStatus: _subscriptionStatus,
      meetingType: _meetingType,
      page: _page,
      perPage: _perPage,
      sort: _sort,
      sortBy: _sortBy,
      sortDir: _sortDir,
      ...rest
    } = filters
    return rest
  }, [filters])

  const accountsMetricsQuery = useClientAccountsMetrics(
    accountsMetricsFilters,
    { enabled: isAccountsTab },
  )
  const { data: accountsMetrics, refetch: refetchAccountsMetrics } =
    accountsMetricsQuery
  // This query polls (sc-1620), which makes two of its flags lie about what the
  // rollup should show. `isError` stays true after a failed poll while the last
  // good figures are cached, so the error state is `isLoadingError`; and
  // `isFetching` is true for every poll, so "dim the cards" is only for a
  // first load or a changed filter set. See `isAccountsMetricsRefreshing`.
  const isAccountsMetricsLoading =
    isAccountsMetricsRefreshing(accountsMetricsQuery)
  const isAccountsMetricsError = accountsMetricsQuery.isLoadingError

  // sc-1329 — always the current PAGE now, on every tab. The radar columns are
  // toggleable anywhere, so this still runs off the radar tab; what changed is
  // that it is never handed a book. `rollupCounts` is no longer taken from it —
  // the gauge is computed server-side over the whole filtered population, which
  // a page could never produce.
  const { radarClients } = useRothRadarBook(paginatedClients.items)

  // The tab's gauge. Deliberately fed `filters` including the tier and status
  // keys: the server ignores them, which is what keeps each card computed over
  // the population its own click starts from rather than the one it produces.
  // No loading flag is threaded through: `keepPreviousData` holds the previous
  // figures while a new page or filter is in flight, so the gauge never blanks
  // and has nothing to render a spinner for.
  // Strip what the gauge must not see. `page`/`perPage`/`sort`/`sortBy` are
  // rejected outright by the metrics DTO (a rollup has no page or sort), and
  // the two card filters are ignored by the server anyway. Leaving any of them
  // in would also refetch the aggregate on every page click and every card
  // click, since the whole filters object is the query key — the same reason
  // `accountsMetricsFilters` strips its own.
  const rothRadarMetricsFilters: ClientFilters = React.useMemo(() => {
    const {
      page: _page,
      perPage: _perPage,
      sort: _sort,
      sortBy: _sortBy,
      sortDir: _sortDir,
      rothRadarSignalState: _signalState,
      rothRadarAlertStatus: _alertStatus,
      rothRadarSetupState: _setupState,
      rothRadarConnectionHealth: _connectionHealth,
      rothRadarPlan: _plan,
      rothRadarScoping: _scoping,
      ...rest
    } = filters
    return rest
  }, [filters])

  const {
    data: rothRadarMetrics,
    isError: isRothRadarMetricsError,
    refetch: refetchRothRadarMetrics,
  } = useRothRadarMetrics(rothRadarMetricsFilters, { enabled: isRothRadarTab })

  const radarByClientId = React.useMemo(
    () =>
      new Map(radarClients.map((radarClient) => [radarClient.id, radarClient])),
    [radarClients],
  )

  // sc-1329 — one branch for every tab. The Roth Radar tab used to slice a
  // client-side filtered array here, because server-side pagination totals
  // reflected the unfiltered book; its filters are now part of that query, so
  // the server's page IS the filtered page.
  const tableClients = React.useMemo(
    () =>
      paginatedClients.items.map((client) => ({
        ...client,
        rothRadar: radarByClientId.get(client.id),
      })),
    [paginatedClients.items, radarByClientId],
  )

  // Mutations
  const toggleFavorite = useToggleFavorite()
  const updateClient = useUpdateClient()
  const updateTeam = useUpdateClientTeam()
  const updateRadarStrategy = useUpdateClientRothRadarStrategy()

  // Server-side sort handler — maps column IDs to API sortBy params
  const handleSortingChange = React.useCallback((sorting: SortingState) => {
    if (sorting.length > 0) {
      const { id, desc } = sorting[0]
      const apiSortBy = COLUMN_SORT_MAP[id]
      setSortBy(apiSortBy)
      setSortDir(apiSortBy ? (desc ? 'desc' : 'asc') : undefined)
      setCurrentPage(1)
    } else {
      setSortBy(undefined)
      setSortDir(undefined)
      setCurrentPage(1)
    }
  }, [])

  // Column actions
  const columnActions = React.useMemo(
    () => ({
      onToggleFavorite: (client: Client) => {
        if (!user?.databaseId) return
        setAnimatingClientId(client.id)
        setAnimatingWasFavorited(client.isFavorite)
        setTimeout(() => {
          setAnimatingClientId(null)
          setAnimatingWasFavorited(undefined)
        }, 400)
        toggleFavorite.mutate({
          userId: user.databaseId,
          clientId: client.id,
          isFavorite: !client.isFavorite,
        })
      },
      onUpdateField: (clientId: string, field: string, value: any) => {
        updateClient.mutate({
          clientId,
          payload: { [field]: value },
        })
      },
      onManageTeam: (client: Client) => {
        setManagingTeamClient(client)
        setTeamDialogOpen(true)
      },
      onUpdateRadarStrategy: (
        clientId: string,
        strategy: RothRadarStrategy | null,
      ) => {
        updateRadarStrategy.mutate({ clientId, strategy })
      },
      onOpenRadarPositions: (client: Client) => {
        setPositionsClient(client)
        setPositionsSheetOpen(true)
      },
      animatingClientId,
      animatingWasFavorited,
      isEditMode,
    }),
    [
      user?.databaseId,
      // `.mutate` (useCallback-stable), never the mutation objects — those are
      // new every render, which rebuilds `columns` and remounts every cell on
      // save, wiping the cell's in-progress edit state (sc-1098).
      toggleFavorite.mutate,
      updateClient.mutate,
      updateRadarStrategy.mutate,
      animatingClientId,
      animatingWasFavorited,
      isEditMode,
    ],
  )

  const columns = React.useMemo(() => {
    const base = createColumns(columnActions, Boolean(annualReviews))
    // Bulk-selection checkbox column only exists on the two tabs with a bulk
    // action — Roth Radar's notify (createRothRadarSelectColumn) and, since
    // sc-1514, Annual Review's approve (createAnnualReviewSelectColumn).
    if (isRothRadarTab) return [createRothRadarSelectColumn(), ...base]
    if (isAnnualReviewTab) return [createAnnualReviewSelectColumn(), ...base]
    return base
  }, [columnActions, annualReviews, isRothRadarTab, isAnnualReviewTab])

  // Tab-aware row-selection gate (sc-1514) — threaded into ClientsTable's
  // `enableRowSelection`. Roth Radar keeps its original "has a connected
  // Plaid account" rule; Annual Review gates on the client's CURRENT open
  // cycle being IN_PROGRESS (bulk approve only ever targets an open,
  // in-progress cycle — QUEUED/no-cycle rows have nothing to approve). Every
  // other tab returns false, though it's moot there: neither `select` column
  // renders outside these two tabs, so `enableRowSelection` is never consulted.
  const isRowSelectable = React.useCallback(
    (client: Client) => {
      if (isRothRadarTab) return client.rothRadar?.isConnected !== false
      if (isAnnualReviewTab) return client.annualReviewStage === 'IN_PROGRESS'
      return false
    },
    [isRothRadarTab, isAnnualReviewTab],
  )

  // Every client a bulk send would reach under the current filters — lets
  // BulkNotifyDialog offer to widen beyond the current page's selection.
  //
  // sc-1329: this is the ONE thing on the tab that genuinely needs the whole
  // matching book rather than a page, so it is its own request, fetched only
  // while the dialog is open. Folding it into the list or the gauge would put
  // the book back on the wire that this story took off it.
  // Unlike the gauge this KEEPS the tier and status filters — it answers "who
  // would the send reach under the current filters", and those are part of that
  // question. It drops only the paging and sort keys, which the DTO rejects.
  const rothRadarSelectableFilters: ClientFilters = React.useMemo(() => {
    const {
      page: _page,
      perPage: _perPage,
      sort: _sort,
      sortBy: _sortBy,
      sortDir: _sortDir,
      ...rest
    } = filters
    return rest
  }, [filters])

  const exportRothRadarBook = useExportRothRadarBook()
  const handleExportRothRadarBook = React.useCallback(() => {
    const { page: _page, perPage: _perPage, ...exportFilters } = filters
    exportRothRadarBook.mutate(exportFilters)
  }, [exportRothRadarBook, filters])

  const { data: selectableClients = [] } = useRothRadarSelectableClients(
    rothRadarSelectableFilters,
    { enabled: isRothRadarTab && bulkNotifyOpen },
  )

  // id -> display name for the bulk dialog — the bulk preview/run endpoints
  // return clientId only, since they're built to scale to hundreds of clients.
  const clientNameById = React.useMemo(
    () =>
      new Map(
        selectableClients.map((client) => [
          client.id,
          `${client.firstName} ${client.lastName ?? ''}`.trim(),
        ]),
      ),
    [selectableClients],
  )

  const allMatchingSelectableIds = React.useMemo(
    () => selectableClients.map((client) => client.id),
    [selectableClients],
  )

  // Selected clients on the CURRENT page only, derived from `tableClients`
  // (not just the raw rowSelection dict) so a stale key can never outlive
  // what's actually visible/selectable right now. Widening to the whole
  // matching set (via `allMatchingSelectableIds`) is now handled inside
  // BulkNotifyDialog itself, not here. Shared by both bulk flows (Roth
  // Radar notify and, since sc-1514, Annual Review approve) — the latter
  // also needs the full `Client` rows (not just ids) for its confirm
  // dialog's name list.
  const selectedTableClients = React.useMemo(
    () => tableClients.filter((client) => rowSelection[client.id]),
    [tableClients, rowSelection],
  )
  const selectedClientIds = React.useMemo(
    () => selectedTableClients.map((client) => client.id),
    [selectedTableClients],
  )

  // Used by the sticky BulkNotifyActionBar's/BulkApproveAnnualReviewsBar's
  // Clear buttons.
  const handleClearSelection = React.useCallback(() => {
    setRowSelection({})
  }, [])

  const handleSaveTeam = (
    clientId: string,
    data: {
      advisorId: string
      plannerId: string | null
      specialistId: string | null
      teamMemberIds: Array<string>
    },
  ) => {
    updateClient.mutate({
      clientId,
      payload: {
        advisorId: data.advisorId,
        plannerId: data.plannerId,
        specialistId: data.specialistId,
      },
    })
    updateTeam.mutate({ clientId, teamMemberIds: data.teamMemberIds })
  }

  // Save / update view handlers
  const handleSaveView = (name: string, isDefault: boolean) => {
    const currentFilters = {
      clientType,
      statusFilter: statusFilter as ClientStatus | 'all',
      // The just-typed text, not the (possibly still-debouncing) nuqs value
      // — a saved view should capture what's actually in the search box.
      searchValue: searchInput,
      advisorIds,
      paidStatus: (paidStatusFilter as ClientPaidStatus | null) ?? null,
      experience: (experienceFilter as ClientExperienceType | null) ?? null,
      meetingType: meetingTypeFilter ?? null,
      subscriptionStatus:
        (subscriptionStatusFilter as
          | ClientFilters['subscriptionStatus']
          | null) ?? null,
      plannerIds,
      specialistIds,
      rcOwnerIds,
      createdFrom: createdFromFilter ?? null,
      createdTo: createdToFilter ?? null,
      schedules: schedulesFilter,
      epActivatedFrom: epActivatedFromFilter ?? null,
      epActivatedTo: epActivatedToFilter ?? null,
      epDeliveredFrom: epDeliveredFromFilter ?? null,
      epDeliveredTo: epDeliveredToFilter ?? null,
    }

    if (activeView) {
      // Update existing view
      updateView(activeView.id, currentFilters, columnVisibility)
      if (name !== activeView.name) renameView(activeView.id, name)
      if (isDefault) toggleDefault(activeView.id)
    } else {
      // Create new view
      saveView(name, currentFilters, columnVisibility, isDefault)
    }
  }

  const handleDeleteView = (viewId: string) => {
    deleteView(viewId)
    // If we're on the deleted view, switch to 'all'
    if (activeTab === viewId) {
      handleTabChange('all')
    }
  }

  return (
    <div className="py-4">
      <PageTitle title="Clients" />
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Clients</h1>
          <p className="text-muted-foreground mt-2">
            Manage and track all your clients in one place. Click any row to
            view client details, or use Edit mode to update fields inline.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* Single "Link New Client" entry (sc-1034). When the annualReviews
              flag is ON it opens the unified wizard (Identify -> Sync ->
              returning-client decision -> branch); when OFF it falls back to
              the simple LinkNewClientDialog so the returning-client flow stays
              dark. No more tab-conditional "Link Returning Client" button. */}
          <Button
            type="button"
            onClick={() => {
              // Plain button click — not a palette handoff, so no email
              // prefill (clears any stale value from a prior Cmd+K request).
              setLinkPrefillEmail(undefined)
              annualReviews ? setWizardOpen(true) : setLinkDialogOpen(true)
            }}
            className="gap-2"
          >
            <PlusCircle className="h-4 w-4" />
            <span className="hidden sm:inline">Link New Client</span>
            <span className="sm:hidden">New</span>
          </Button>
        </div>
      </div>

      <ClientsTable
        data={tableClients}
        columns={columns}
        isLoading={isLoading}
        // sc-1329 — the radar tab used to be excluded here because it had no
        // query of its own. It does now, and a failed fetch must not render as
        // an indistinguishable "No clients found".
        isError={isClientsError || (isRothRadarTab && isRothRadarMetricsError)}
        onRetry={() => {
          void refetchClients()
          if (isRothRadarTab) void refetchRothRadarMetrics()
        }}
        isEditMode={isEditMode}
        activeTab={activeTab}
        defaultTab={defaultTab}
        onTabChange={handleTabChange}
        onStatusFilterChange={(v) =>
          void setStatusFilter(v === 'all' ? 'all' : v)
        }
        onSearchChange={(v) => setSearchInput(v)}
        advisorIds={advisorIds}
        onAdvisorChange={(ids) => void setAdvisorIds(ids)}
        paidStatus={paidStatusFilter as ClientPaidStatus | null}
        onPaidStatusChange={(v) => void setPaidStatusFilter(v)}
        experience={experienceFilter as ClientExperienceType | null}
        onExperienceChange={(v) => void setExperienceFilter(v)}
        meetingType={meetingTypeFilter}
        onMeetingTypeChange={(v) => void setMeetingTypeFilter(v)}
        subscriptionStatus={
          (subscriptionStatusFilter as NonNullable<
            ClientFilters['subscriptionStatus']
          > | null) ?? null
        }
        onSubscriptionStatusChange={(v) => void setSubscriptionStatusFilter(v)}
        plannerIds={plannerIds}
        onPlannerChange={(ids) => void setPlannerIds(ids)}
        specialistIds={specialistIds}
        onSpecialistChange={(ids) => void setSpecialistIds(ids)}
        rcOwnerIds={rcOwnerIds}
        onRcOwnerChange={(ids) => void setRcOwnerIds(ids)}
        createdFrom={createdFromFilter ?? null}
        createdTo={createdToFilter ?? null}
        onCreatedRangeChange={(range) => {
          void setCreatedFromFilter(range.from)
          void setCreatedToFilter(range.to)
        }}
        schedules={schedulesFilter}
        onSchedulesChange={(values) => void setSchedulesFilter(values)}
        epActivatedFrom={epActivatedFromFilter ?? null}
        epActivatedTo={epActivatedToFilter ?? null}
        onEpActivatedRangeChange={(range) => {
          void setEpActivatedFromFilter(range.from)
          void setEpActivatedToFilter(range.to)
        }}
        epDeliveredFrom={epDeliveredFromFilter ?? null}
        epDeliveredTo={epDeliveredToFilter ?? null}
        onEpDeliveredRangeChange={(range) => {
          void setEpDeliveredFromFilter(range.from)
          void setEpDeliveredToFilter(range.to)
        }}
        statusFilter={statusFilter as ClientStatus | 'all'}
        searchValue={searchInput}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        columnOrder={columnOrder}
        onColumnOrderChange={setColumnOrder}
        views={views}
        onSaveView={() => setSaveDialogOpen(true)}
        onDeleteView={handleDeleteView}
        onToggleDefault={toggleDefault}
        onEditModeChange={setIsEditMode}
        rothRadarCounts={rothRadarMetrics?.counts}
        rothRadarStatusFilter={rothRadarStatusFilter}
        rothRadarTierFilter={rothRadarTierFilter}
        rothRadarSetupFilter={rothRadarSetupFilter}
        rothRadarConnectionFilter={rothRadarConnectionFilter}
        rothRadarPlanFilter={rothRadarPlanFilter}
        rothRadarScopingFilter={rothRadarScopingFilter}
        onRothRadarStatusFilterChange={setRothRadarStatusFilter}
        onRothRadarTierFilterChange={setRothRadarTierFilter}
        onRothRadarSetupFilterChange={setRothRadarSetupFilter}
        onRothRadarConnectionFilterChange={setRothRadarConnectionFilter}
        onRothRadarPlanFilterChange={setRothRadarPlanFilter}
        onRothRadarScopingFilterChange={setRothRadarScopingFilter}
        onExportRothRadarBook={handleExportRothRadarBook}
        isExportingRothRadarBook={exportRothRadarBook.isPending}
        accountsMetrics={accountsMetrics}
        isAccountsMetricsLoading={isAccountsMetricsLoading}
        isAccountsMetricsError={isAccountsMetricsError}
        onAccountsMetricsRetry={() => void refetchAccountsMetrics()}
        annualReviewBookClients={isAnnualReviewTab ? fullBook.items : []}
        isAnnualReviewBookLoading={isAnnualReviewTab && isLoadingFullBook}
        dueWithin={
          dueWithinFilter
            ? (Number(dueWithinFilter) as ClientArDueWithinFilter)
            : null
        }
        onDueWithinChange={(value) =>
          void setDueWithinFilter(
            value ? (String(value) as '30' | '60' | '90') : null,
          )
        }
        arYear={arYearFilter}
        arStatus={(arStatusFilter as ClientArStatusFilter | null) ?? null}
        onArYearStatusChange={(year, status) => {
          void setArYearFilter(year)
          void setArStatusFilter(status)
        }}
        overdueOnly={overdueOnly}
        onOverdueOnlyChange={(value) => void setOverdueOnly(value)}
        arStage={(arStageFilter as ClientArStageFilter | null) ?? null}
        onArStageChange={(value) => void setArStageFilter(value)}
        annualReviews={Boolean(annualReviews)}
        onSortingChange={handleSortingChange}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        isRowSelectable={isRowSelectable}
        pagination={{
          currentPage: paginatedClients.page,
          totalPages: paginatedClients.totalPages,
          perPage: pageSize,
          total: paginatedClients.total,
          onPageChange: setCurrentPage,
          onPageSizeChange: handlePageSizeChange,
        }}
      />

      {isRothRadarTab && (
        <BulkNotifyActionBar
          selectedCount={selectedClientIds.length}
          onClear={handleClearSelection}
          onNotify={() => setBulkNotifyOpen(true)}
        />
      )}

      <BulkNotifyDialog
        open={bulkNotifyOpen}
        onOpenChange={setBulkNotifyOpen}
        clientIds={selectedClientIds}
        allMatchingClientIds={allMatchingSelectableIds}
        clientNameById={clientNameById}
        onStarted={handleClearSelection}
      />

      {isAnnualReviewTab && (
        <BulkApproveAnnualReviewsBar
          selectedCount={selectedClientIds.length}
          onClear={handleClearSelection}
          onApprove={() => setBulkApproveOpen(true)}
        />
      )}

      <BulkApproveAnnualReviewsDialog
        open={bulkApproveOpen}
        onOpenChange={setBulkApproveOpen}
        clients={selectedTableClients}
        onApproved={handleClearSelection}
      />

      <SaveViewDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        onSave={handleSaveView}
        existingName={activeView?.name}
      />

      <LinkNewClientDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        onClientCreated={(clientId) => setCreatedClientId(clientId)}
        prefillEmail={linkPrefillEmail}
      />

      <TeamManagementDialog
        client={managingTeamClient}
        open={teamDialogOpen}
        onOpenChange={(open) => {
          setTeamDialogOpen(open)
          if (!open) {
            setCreatedClientId(null)
          }
        }}
        onSave={handleSaveTeam}
      />

      {/* Entirely part of the Annual Review / returning-client feature — the
          button that opens it is already hidden when the flag is off (baked
          into `isAnnualReviewTab`), but the wizard itself is gated too for
          defense. */}
      {Boolean(annualReviews) && (
        <AddMissingClientWizard
          open={wizardOpen}
          onOpenChange={setWizardOpen}
          prefillEmail={linkPrefillEmail}
        />
      )}

      {positionsClient && (
        <RothRadarPositionsSheet
          clientId={positionsClient.id}
          clientName={`${positionsClient.firstName} ${positionsClient.lastName}`.trim()}
          open={positionsSheetOpen}
          onOpenChange={setPositionsSheetOpen}
        />
      )}

      {/* Save status FAB — visible only in edit mode */}
      {isEditMode && (
        <SaveStatusFab status={saveStatus} onSave={handleFabSave} />
      )}
    </div>
  )
}
